import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";

/** Below this, "Reveal all" is not offered: the reel is still the better experience at one or
 *  two prizes, and a second button there is noise heavier than what it saves. */
export const REVEAL_ALL_THRESHOLD = 3;

/**
 * The reveal choice: reveal one prize with the full ceremony, or reveal everything at once.
 *
 * Reveal stays the **primary** action — the reel is the default experience and "Reveal all" is an
 * exit for whoever wants one, not the recommended way through. One component so the in-widget card
 * and the in-game launcher cannot drift on the threshold or the copy.
 */
export const RevealActions = ({
  pendingCount,
  onRevealOne,
  onRevealAll,
  className,
}: {
  pendingCount: number;
  onRevealOne: () => void;
  onRevealAll: () => void;
  className?: string;
}) => {
  const { t } = useTranslation();

  return (
    <div className={cn("flex w-full flex-col gap-3", className)}>
      <button
        type="button"
        onClick={onRevealOne}
        className="bg-gradient-cta relative flex min-h-12 w-full items-center justify-center gap-2 rounded-full px-6 py-3.5 font-heading text-[15px] leading-none font-bold text-background transition-[filter] hover:brightness-105"
      >
        {t("igwButton.revealPrize", "Reveal Prize")}
        <ArrowRight className="size-4 shrink-0" strokeWidth={2.5} />
        {/* Count badge, mirroring the in-game reveal button: only above 1 — a "1" badge is noise
            when the caption already says so. It is also the cue that makes the secondary action
            worth reading. */}
        {pendingCount > 1 && (
          <span
            className="absolute -top-2 -right-2 flex size-6 items-center justify-center rounded-full bg-foreground text-[12px] font-bold text-background"
            style={{ boxShadow: "0 0 10px 0 var(--prizes)" }}
          >
            {pendingCount}
          </span>
        )}
      </button>

      {pendingCount >= REVEAL_ALL_THRESHOLD && (
        <button
          type="button"
          onClick={onRevealAll}
          className="flex min-h-11 w-full items-center justify-center font-body text-[13px] font-medium text-foreground-secondary underline underline-offset-[3px]"
        >
          {/* No count: the primary button's badge right above already carries it. */}
          {t("prizeReveal.revealAll", "Reveal all")}
        </button>
      )}
    </div>
  );
};
